import { hasMinimumEnteredWarehouseVariantValues } from '@gonfalon/experiments/assignment-source-variants';
import { convertFormToExperimentAnalysisConfig } from '@gonfalon/experiments/draft-experiment-form';
import type {
  AssignmentDataSourceWindowInput,
  MetricInput,
  UpdateExperimentFieldsValue,
} from '@gonfalon/experiments/instructions';
import {
  makeFieldUpdate,
  makeSaveAndStartNewIterationInstruction,
  makeUpdateExperimentFieldsInstruction,
} from '@gonfalon/experiments/instructions';
import { isCupedV2Eligible } from '@gonfalon/experiments/isCupedV2Eligible';
import { isDraftExperiment } from '@gonfalon/experiments/isDraftExperiment';
import { isWarehouseAssignmentMode } from '@gonfalon/experiments/isWarehouseAssignmentMode';
import type {
  AssignmentDataSourceInput,
  DraftExperimentFormProps,
  DraftExperimentIterationProps,
  Experiment,
  FlagsInput,
  SampleSizeTarget,
  TreatmentsInput,
} from '@gonfalon/experiments/types';
import { ExperimentDataSourceKind, ExperimentMethodologyKind, FieldActionKind } from '@gonfalon/experiments/types';
import { useUpdateExperiment } from '@gonfalon/rest-api/hooks/useUpdateExperiment';
import { isEqual } from 'lodash';

import type { ZQuantileFn } from '#components/experimentation/common/components/PowerCalculator/powerAnalysis';
import {
  loadZQuantile,
  resizeTargetForDesign,
} from '#components/experimentation/common/components/PowerCalculator/powerAnalysis';

import { createFlagsInput } from './useCreateExperimentIteration/createFlagsInput';
import { redistributeTreatments } from './useCreateExperimentIteration/redistributeTreatments';
import { transformTreatments } from './useCreateExperimentIteration/transformTreatments';

const MILLISECONDS_PER_HOUR = 3600000;

type UseUpdateExperimentWithFieldsProps = {
  projectKey: string;
  environmentKey: string;
  experimentKey: string;
  defaultValues: DraftExperimentFormProps;
  experiment?: Experiment;
};

const handleMetricsAndPrimaryChange = (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
): UpdateExperimentFieldsValue => {
  const value: UpdateExperimentFieldsValue = {};

  if (updatedValues.methodology === ExperimentMethodologyKind.EXPORT_ONLY) {
    return {};
  }

  const metricsChanged = !isEqual(updatedValues.iterationProps?.metrics, defaultValues.iterationProps?.metrics);
  const newPrimaryMetricKey = updatedValues.iterationProps?.primaryMetric?.key;
  const oldPrimaryMetricKey = defaultValues.iterationProps?.primaryMetric?.key;
  const newPrimaryFunnelKey = updatedValues.iterationProps?.primaryFunnel?.key;
  const oldPrimaryFunnelKey = defaultValues.iterationProps?.primaryFunnel?.key;
  const primaryMetricChanged = newPrimaryMetricKey !== oldPrimaryMetricKey;
  const primaryFunnelChanged = newPrimaryFunnelKey !== oldPrimaryFunnelKey;

  if (!metricsChanged && !primaryMetricChanged && !primaryFunnelChanged) {
    return {};
  }

  const primaryMetric = updatedValues.iterationProps?.primaryMetric;
  const primaryFunnel = updatedValues.iterationProps?.primaryFunnel;

  // When primary metric changes, check isGroup to determine which field to set
  if (primaryMetricChanged && primaryMetric) {
    if (primaryMetric.isGroup) {
      value.primaryFunnelKey = makeFieldUpdate(FieldActionKind.UPDATE, primaryMetric.key);
    } else {
      value.primarySingleMetricKey = makeFieldUpdate(FieldActionKind.UPDATE, primaryMetric.key);
    }
  }

  // When primary funnel changes, set it
  if (primaryFunnelChanged && primaryFunnel) {
    value.primaryFunnelKey = makeFieldUpdate(FieldActionKind.UPDATE, primaryFunnel.key);
  }

  const baseMetrics: MetricInput[] =
    updatedValues.iterationProps?.metrics?.map((m) => ({
      key: m.key,
      isGroup: m.isGroup,
      ...(m.specifiedAnalysisUnit ? { analysisUnit: m.specifiedAnalysisUnit } : {}),
    })) ?? [];

  let metrics = baseMetrics;

  // When primary metric/funnel changes, ensure it's included in the metrics list
  if (primaryMetric && !baseMetrics.some((m) => m.key === primaryMetric.key)) {
    metrics = [
      {
        key: primaryMetric.key,
        isGroup: primaryMetric.isGroup,
        ...(primaryMetric.specifiedAnalysisUnit ? { analysisUnit: primaryMetric.specifiedAnalysisUnit } : {}),
      },
      ...metrics,
    ];
  }

  if (primaryFunnel && !metrics.some((m) => m.key === primaryFunnel.key)) {
    metrics = [
      {
        key: primaryFunnel.key,
        isGroup: primaryFunnel.isGroup,
        ...(primaryFunnel.specifiedAnalysisUnit ? { analysisUnit: primaryFunnel.specifiedAnalysisUnit } : {}),
      },
      ...metrics,
    ];
  }

  value.metrics = makeFieldUpdate(FieldActionKind.UPDATE, metrics);

  return value;
};

const handleTreatmentsChange = (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
  experiment: Experiment | undefined,
): UpdateExperimentFieldsValue => {
  const treatmentsChanged =
    !isEqual(updatedValues.iterationProps?.treatmentAllocations, defaultValues.iterationProps?.treatmentAllocations) ||
    updatedValues.iterationProps?.controlVariationId !== defaultValues.iterationProps?.controlVariationId ||
    updatedValues.iterationProps?.experimentAllocation !== defaultValues.iterationProps?.experimentAllocation;

  if (!treatmentsChanged || !experiment) {
    return {};
  }

  const isWarehouseAssignment = isWarehouseAssignmentMode(updatedValues);
  // Incomplete warehouse treatments (placeholder rows or a partial edit) stay out of the
  // PATCH until two source values exist. Convert from a flag still sends treatments: [] so
  // flag treatments are cleared with flags and assignmentDataSource.
  if (
    isWarehouseAssignment &&
    !hasMinimumEnteredWarehouseVariantValues(updatedValues.iterationProps?.treatmentAllocations)
  ) {
    return isWarehouseAssignmentMode(defaultValues) ? {} : { treatments: makeFieldUpdate(FieldActionKind.UPDATE, []) };
  }

  const originalIteration = experiment.draftIteration ?? experiment.currentIteration;
  const baseTreatments = transformTreatments(originalIteration?.treatments ?? []);

  const treatments: TreatmentsInput = updatedValues.iterationProps?.treatmentAllocations
    ? redistributeTreatments(
        updatedValues.iterationProps.treatmentAllocations,
        updatedValues.iterationProps.controlVariationId,
        updatedValues.iterationProps.experimentAllocation,
        isWarehouseAssignment,
      )
    : baseTreatments;

  return { treatments: makeFieldUpdate(FieldActionKind.UPDATE, treatments) };
};

const handleFlagsChange = async (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
  projectKey: string,
  environmentKey: string,
  experiment: Experiment | undefined,
): Promise<UpdateExperimentFieldsValue> => {
  if (isWarehouseAssignmentMode(updatedValues)) {
    const wasWarehouseAssignment = isWarehouseAssignmentMode(defaultValues);
    if (!wasWarehouseAssignment || defaultValues.iterationProps?.flagConfig) {
      return { flags: makeFieldUpdate(FieldActionKind.UPDATE, {}) };
    }
    return {};
  }

  if (
    isEqual(updatedValues.iterationProps?.flagConfig, defaultValues.iterationProps?.flagConfig) ||
    !updatedValues.iterationProps?.flagConfig
  ) {
    return {};
  }

  const flags: FlagsInput = await createFlagsInput({
    flagConfig: updatedValues.iterationProps.flagConfig,
    projectKey,
    environmentKey,
  });

  const isStopped = experiment?.currentIteration?.status === 'stopped';

  if (isStopped) {
    return { flagSettings: makeFieldUpdate(FieldActionKind.UPDATE, flags) };
  }

  return { flags: makeFieldUpdate(FieldActionKind.UPDATE, flags) };
};

const handleExperimentLevelFields = (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
  experiment: Experiment | undefined,
): UpdateExperimentFieldsValue => {
  const value: UpdateExperimentFieldsValue = {};

  if (updatedValues.experimentName !== defaultValues.experimentName && updatedValues.experimentName) {
    value.name = makeFieldUpdate(FieldActionKind.UPDATE, updatedValues.experimentName);
  }

  if (updatedValues.hypothesis !== defaultValues.hypothesis && updatedValues.hypothesis) {
    value.description = makeFieldUpdate(FieldActionKind.UPDATE, updatedValues.hypothesis);
  }

  // Export-only experiments only support the LaunchDarkly data source, but the data source is
  // only mutable while the experiment is still a draft
  const dataSource =
    updatedValues.methodology === ExperimentMethodologyKind.EXPORT_ONLY && isDraftExperiment(experiment)
      ? ExperimentDataSourceKind.LAUNCHDARKLY
      : updatedValues.dataSource;

  if (dataSource !== defaultValues.dataSource && dataSource) {
    value.dataSource = makeFieldUpdate(FieldActionKind.UPDATE, dataSource);
  }

  if (updatedValues.holdoutId !== defaultValues.holdoutId) {
    value.holdoutId = makeFieldUpdate(FieldActionKind.UPDATE, updatedValues.holdoutId || null);
  }

  return value;
};

const handleMethodologyFields = (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
): UpdateExperimentFieldsValue => {
  const value: UpdateExperimentFieldsValue = {};

  const methodologyChanged =
    updatedValues.methodology !== defaultValues.methodology ||
    !isEqual(updatedValues.analysisConfig, defaultValues.analysisConfig) ||
    !isEqual(updatedValues.mcc, defaultValues.mcc) ||
    updatedValues.isSequentialTesting !== defaultValues.isSequentialTesting ||
    updatedValues.uninformativePriorsEnabled !== defaultValues.uninformativePriorsEnabled;

  if (methodologyChanged) {
    const methodology = updatedValues.methodology ?? 'bayesian';
    if (updatedValues.methodology !== defaultValues.methodology) {
      value.methodology = makeFieldUpdate(FieldActionKind.UPDATE, methodology);
    }

    if (methodology !== ExperimentMethodologyKind.EXPORT_ONLY) {
      const analysisConfig = convertFormToExperimentAnalysisConfig(
        updatedValues.analysisConfig,
        updatedValues.mcc,
        updatedValues.isSequentialTesting,
        methodology,
        updatedValues.uninformativePriorsEnabled,
      );
      if (analysisConfig) {
        value.analysisConfig = makeFieldUpdate(FieldActionKind.UPDATE, analysisConfig);
      }
    }
  }

  return value;
};

// Only an explicit `false` detaches the covariate; an absent flag leaves whatever the form carries.
const getNextCovariateId = (iterationProps: DraftExperimentIterationProps | undefined): string | null => {
  if (iterationProps?.deletedCovariateId) {
    return null;
  }

  if (iterationProps?.isUsingStratifiedSampling === false) {
    return null;
  }

  return iterationProps?.covariateInfo?.id ?? null;
};

const getReallocationFrequencyMillis = (iterationProps: DraftExperimentIterationProps | undefined): number | null => {
  const hours = iterationProps?.reallocationFrequencyHours;

  if (typeof hours !== 'number') {
    return null;
  }

  return hours * MILLISECONDS_PER_HOUR;
};

// A target only applies to a fixed-horizon frequentist experiment, and the API rejects one that
// does not. Switching methodology in the same save therefore detaches rather than sending it on.
const getNextSampleSizeTarget = (
  updatedValues: Pick<
    DraftExperimentFormProps,
    'methodology' | 'isSequentialTesting' | 'sampleSizeTarget' | 'iterationProps' | 'analysisConfig'
  >,
  zQuantile: ZQuantileFn | undefined,
): SampleSizeTarget | null => {
  const supportsSampleSizeTarget =
    updatedValues.methodology === ExperimentMethodologyKind.FREQUENTIST && !updatedValues.isSequentialTesting;

  if (!supportsSampleSizeTarget) {
    return null;
  }

  // A target sizes the primary metric, so with none left it sizes nothing. A funnel stands in, but
  // only ever measures a conversion rate, so a numeric target does not survive one.
  const { primaryMetric, primaryFunnel } = updatedValues.iterationProps ?? {};
  if (!primaryMetric && !primaryFunnel) {
    return null;
  }
  if (!primaryMetric && updatedValues.sampleSizeTarget?.inputs.metricType !== 'conversion') {
    return null;
  }

  // A target's inputs cannot disagree with the experiment, but its sample size and duration were
  // computed from the audience percentage and statistical settings as they stood when it was saved,
  // so what gets stored is those numbers recomputed against the design being saved alongside them.
  const resized = resizeTargetForDesign(
    updatedValues.sampleSizeTarget,
    {
      significanceThreshold: updatedValues.analysisConfig?.significanceThreshold,
      testDirection: updatedValues.analysisConfig?.testDirection,
      experimentAllocation: updatedValues.iterationProps?.experimentAllocation,
    },
    zQuantile,
  );

  return resized ?? null;
};

const handleSimpleIterationFields = (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
  zQuantile: ZQuantileFn | undefined,
): UpdateExperimentFieldsValue => {
  const value: UpdateExperimentFieldsValue = {};

  if (updatedValues.hypothesis !== defaultValues.hypothesis && updatedValues.hypothesis) {
    value.hypothesis = makeFieldUpdate(FieldActionKind.UPDATE, updatedValues.hypothesis);
  }

  if (
    updatedValues.iterationProps?.randomizationUnit !== defaultValues.iterationProps?.randomizationUnit &&
    updatedValues.iterationProps?.randomizationUnit
  ) {
    value.randomizationUnit = makeFieldUpdate(FieldActionKind.UPDATE, updatedValues.iterationProps.randomizationUnit);
  }

  // Attribute updates of any shape are rejected for export-only iterations
  if (
    updatedValues.methodology !== ExperimentMethodologyKind.EXPORT_ONLY &&
    !isEqual(updatedValues.iterationProps?.attributes, defaultValues.iterationProps?.attributes)
  ) {
    value.attributes = makeFieldUpdate(FieldActionKind.UPDATE, updatedValues.iterationProps?.attributes ?? []);
  }

  if (updatedValues.iterationProps?.cannotReshuffleTraffic !== defaultValues.iterationProps?.cannotReshuffleTraffic) {
    value.canReshuffleTraffic = makeFieldUpdate(
      FieldActionKind.UPDATE,
      !updatedValues.iterationProps?.cannotReshuffleTraffic,
    );
  }

  const newCovariateId = getNextCovariateId(updatedValues.iterationProps);
  const oldCovariateId = defaultValues.iterationProps?.covariateInfo?.id ?? null;
  if (newCovariateId !== oldCovariateId) {
    value.covariateId = makeFieldUpdate(FieldActionKind.UPDATE, newCovariateId);
  }

  if (
    updatedValues.iterationProps?.reallocationFrequencyHours !==
    defaultValues.iterationProps?.reallocationFrequencyHours
  ) {
    value.reallocationFrequencyMillis = makeFieldUpdate(
      FieldActionKind.UPDATE,
      getReallocationFrequencyMillis(updatedValues.iterationProps),
    );
  }

  // Only patch cupedEnabled when CUPED V2 eligible. If the field is present on a non-eligible
  // experiment (any value), the backend rejects with "field cupedEnabled is not supported".
  // Switching away from a V2-eligible source recomputes cupedEnabled server-side via UpdateDataSource.
  if (
    updatedValues.methodology !== ExperimentMethodologyKind.EXPORT_ONLY &&
    isCupedV2Eligible(updatedValues.dataSource)
  ) {
    const nextCupedEnabled = updatedValues.cupedEnabled ?? false;
    if (nextCupedEnabled !== (defaultValues.cupedEnabled ?? false)) {
      value.cupedEnabled = makeFieldUpdate(FieldActionKind.UPDATE, nextCupedEnabled);
    }
  }

  const nextSampleSizeTarget = getNextSampleSizeTarget(updatedValues, zQuantile);

  if (!isEqual(nextSampleSizeTarget, defaultValues.sampleSizeTarget ?? null)) {
    value.sampleSizeTarget = makeFieldUpdate(FieldActionKind.UPDATE, nextSampleSizeTarget);
  }

  return value;
};

const toAssignmentDataSourceInput = (
  assignmentSource: DraftExperimentIterationProps['assignmentSource'],
): AssignmentDataSourceInput | undefined => {
  if (!assignmentSource?.key) {
    return undefined;
  }

  // The experiment ID is only required to start the experiment, so send an empty one to let a draft
  // save the source on its own.
  return {
    key: assignmentSource.key,
    experimentId: assignmentSource.assignmentSourceExperimentId ?? '',
    ...(assignmentSource.windowStart !== undefined && assignmentSource.windowStart !== null
      ? { startTime: assignmentSource.windowStart }
      : {}),
    ...(assignmentSource.windowEnd !== undefined && assignmentSource.windowEnd !== null
      ? { endTime: assignmentSource.windowEnd }
      : {}),
  };
};

const toAssignmentDataSourceWindowInput = (
  assignmentSource: DraftExperimentIterationProps['assignmentSource'],
): AssignmentDataSourceWindowInput => ({
  ...(assignmentSource?.windowStart !== undefined && assignmentSource.windowStart !== null
    ? { startTime: assignmentSource.windowStart }
    : {}),
  ...(assignmentSource?.windowEnd !== undefined && assignmentSource.windowEnd !== null
    ? { endTime: assignmentSource.windowEnd }
    : {}),
});

const handleAssignmentDataSourceChange = (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
  experiment: Experiment | undefined,
): UpdateExperimentFieldsValue => {
  const wasWarehouseAssignment = isWarehouseAssignmentMode(defaultValues);
  const isWarehouseAssignment = isWarehouseAssignmentMode(updatedValues);

  if (!wasWarehouseAssignment && !isWarehouseAssignment) {
    return {};
  }

  // Once an iteration has started, the source it read from is part of the record, so the API only
  // accepts the window through its own field and rejects assignmentDataSource outright. The design
  // page disables the rest of the warehouse fields to match. A draft keeps sending the whole
  // attachment, since assignmentDataSourceWindow is rejected before an iteration exists.
  if (!isDraftExperiment(experiment)) {
    if (!wasWarehouseAssignment || !isWarehouseAssignment) {
      return {};
    }

    const nextWindow = toAssignmentDataSourceWindowInput(updatedValues.iterationProps?.assignmentSource);
    const previousWindow = toAssignmentDataSourceWindowInput(defaultValues.iterationProps?.assignmentSource);
    if (isEqual(nextWindow, previousWindow)) {
      return {};
    }

    return { assignmentDataSourceWindow: makeFieldUpdate(FieldActionKind.UPDATE, nextWindow) };
  }

  if (wasWarehouseAssignment && !isWarehouseAssignment) {
    return { assignmentDataSource: makeFieldUpdate(FieldActionKind.UPDATE, null) };
  }

  const next = toAssignmentDataSourceInput(updatedValues.iterationProps?.assignmentSource);
  const previous = toAssignmentDataSourceInput(defaultValues.iterationProps?.assignmentSource);
  if (!next || isEqual(next, previous)) {
    return {};
  }

  return { assignmentDataSource: makeFieldUpdate(FieldActionKind.UPDATE, next) };
};

export const generateFieldUpdatesValue = async (
  defaultValues: DraftExperimentFormProps,
  updatedValues: DraftExperimentFormProps,
  experiment: Experiment | undefined,
  projectKey: string,
  environmentKey: string,
): Promise<UpdateExperimentFieldsValue> => {
  const experimentLevelFields = handleExperimentLevelFields(defaultValues, updatedValues, experiment);
  const methodologyFields = handleMethodologyFields(defaultValues, updatedValues);

  if (!updatedValues.iterationProps) {
    return {
      ...experimentLevelFields,
      ...methodologyFields,
    };
  }

  // Only pulled in when there is a target to resize, so the power-analysis chunk stays off the save
  // path of every experiment without one. A failed load stores the numbers as they were rather than
  // failing the save.
  const zQuantile = updatedValues.sampleSizeTarget ? await loadZQuantile().catch(() => undefined) : undefined;

  const simpleIterationFields = handleSimpleIterationFields(defaultValues, updatedValues, zQuantile);
  const metricsAndPrimaryFields = handleMetricsAndPrimaryChange(defaultValues, updatedValues);
  const treatmentsFields = handleTreatmentsChange(defaultValues, updatedValues, experiment);
  const flagsFields = await handleFlagsChange(defaultValues, updatedValues, projectKey, environmentKey, experiment);
  const assignmentDataSourceFields = handleAssignmentDataSourceChange(defaultValues, updatedValues, experiment);

  return {
    ...experimentLevelFields,
    ...methodologyFields,
    ...simpleIterationFields,
    ...metricsAndPrimaryFields,
    ...treatmentsFields,
    ...flagsFields,
    ...assignmentDataSourceFields,
  };
};

export const useUpdateExperimentWithFields = (props: UseUpdateExperimentWithFieldsProps) => {
  const { projectKey, environmentKey, experimentKey, defaultValues, experiment } = props;
  const { mutateAsync: updateExperimentMutation } = useUpdateExperiment();

  const updateExperimentWithFields = async (
    updatedValues: DraftExperimentFormProps,
    options?: {
      saveAndStart?: boolean;
      changeJustification?: string;
    },
  ) => {
    const value = await generateFieldUpdatesValue(defaultValues, updatedValues, experiment, projectKey, environmentKey);
    const hasFieldUpdates = Object.keys(value).length > 0;
    const isSaveAndStart = options?.saveAndStart;

    if (isSaveAndStart || hasFieldUpdates) {
      const instruction = isSaveAndStart
        ? makeSaveAndStartNewIterationInstruction(hasFieldUpdates ? value : undefined, options?.changeJustification)
        : makeUpdateExperimentFieldsInstruction(value);

      return updateExperimentMutation({
        projectKey,
        environmentKey,
        experimentKey,
        body: { instructions: [instruction] },
      });
    }
  };

  return {
    updateExperimentWithFields,
  };
};
